import React, { useEffect, useState } from "react";
import { StyleSheet, View, ViewStyle } from "react-native";
import { Title18 } from "@stylesheets";
import { Colors } from "@constants";
import { TestIDs } from "@utils";
import { ControlQuantityBtn } from "./ControlQuantityBtn";
import { useDispatch, useSelector } from "react-redux";
import { GlobalState } from "@redux";
import { ActiveOffer } from "@foodi/core";
import { OfferViewModel, OrderViewModel } from "@modules";
import moment from "moment/moment";
import { useCartSelectors } from "@atomic/organisms/Cart/useCartSelectors";
import { isNil } from "lodash";

interface IProps {
  forwardTestID?: string;
  onHandle: Function;
  productId: string;
  sameProductPerOrder?: number;
  isOverProductsLimit?: boolean;
  quantityValue?: number;
  style?: ViewStyle | ViewStyle[];
  disableDefaultStyle?: boolean;
  addQuantityOverride?: (() => void) | undefined;
  quantityOverall?: number;
  isBakingModal?: boolean;
}

export const ProductQuantity: React.FC<IProps> = React.memo(
  ({
    onHandle,
    productId,
    sameProductPerOrder,
    isOverProductsLimit,
    quantityValue = 0,
    style,
    disableDefaultStyle = false,
    addQuantityOverride,
    quantityOverall = 0,
    isBakingModal = false,
  }) => {
    const [quantity, setQuantity] = useState<number>(quantityValue);
    const isOverOrderLimit =
      sameProductPerOrder === undefined
        ? false
        : quantity >= sameProductPerOrder;

    const addQuantity = () => {
      if (quantity + 1 <= quantityOverall) {
        setQuantity(quantity + 1);
        onHandle(quantity + 1);
      }
    };

    const subtractQuantity = () => {
      setQuantity(quantity - 1);
      onHandle(quantity - 1);
    };

    useEffect(() => {
      if (quantityValue !== quantity) {
        setQuantity(quantityValue);
      }
    }, [quantityValue]);

    const [offerVM] = useState(new OfferViewModel());

    const selectedActiveOffer = useSelector(
      (state: GlobalState) => state.offers.activeOrderableOffer
    );
    const userInfo = useSelector(
      (state: GlobalState) => ({
        idGuest: state.auth.userInfo?.idGuest,
        state: state.auth.userInfo?.state,
        validFrom: state.auth.userInfo?.validFrom,
        validTo: state.auth.userInfo?.validTo,
      })
    );
    const dispatch = useDispatch();
    const orderVM = new OrderViewModel(dispatch, userInfo.idGuest || '');
    const isBadgeExpired = orderVM.isBadgeExpired(userInfo.state, userInfo.validFrom ? moment(userInfo.validFrom) : null, userInfo.validTo ? moment(userInfo.validTo) : null) || false;

    const { badgeNumber, paymentMethodConfig } = useCartSelectors();
    const userHasBadge = !!badgeNumber;

    const isOfferInAdvance = orderVM.isInAdvanceOrder(selectedActiveOffer?.withdrawRange);
    const paymentTypes = paymentMethodConfig?.paymentTypes ? orderVM.getOfferPaymentOptionsBasedOnUser(userHasBadge, paymentMethodConfig, isOfferInAdvance) : [];
    const noPaymentTypeAvailable = paymentTypes.length === 0;

    return (
      <View style={[disableDefaultStyle ? {} : styles.container, style]}>
        <ControlQuantityBtn
          isDisabled={
            offerVM.isActiveOfferReady(selectedActiveOffer as ActiveOffer) ||
            isOverOrderLimit ||
            isOverProductsLimit ||
            quantity === quantityOverall ||
            isBadgeExpired ||
            noPaymentTypeAvailable
          }
          isAddition
          callback={addQuantityOverride ?? addQuantity}
          forwardTestID={`${TestIDs.restaurantDetail.actions.addProductQuantity}_${productId}`}
        />

        {!!quantity && <Title18 style={styles.title}>{quantity}</Title18>}
        {!!quantity && (
          <ControlQuantityBtn
            isDisabled={isBakingModal && quantity === 1}
            isAddition={false}
            callback={subtractQuantity}
            forwardTestID={`${TestIDs.restaurantDetail.actions.subtractProductQuantity}_${productId}`}
          />
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
    width: 80,
    height: 25,
    flexDirection: "row-reverse",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  title: {
    color: Colors.black,
    fontFamily: "manrope-bold",
    textAlign: "center",
  },
});
